import { useQuery } from '@tanstack/react-query'

import type { ClinicId } from '@snootsvet/shared'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'

/**
 * Fetches blocked slots for a date range. When clinicId is provided, the API
 * returns only blocks relevant to that clinic's scheduled vets and local dates.
 */
export function useListBlockedSlots(opts: {
  /** Start date (YYYY-MM-DD, inclusive). Query is disabled until both from and to are provided. */
  from?: string
  /** End date (YYYY-MM-DD, exclusive). Query is disabled until both from and to are provided. */
  to?: string
  staffIds?: string[]
  /**
   * Scheduling context, not a direct blocked-slot clinicId filter. Global blocks
   * are included only when the vet works at this clinic on the block's date;
   * locum blocks are limited to their originating clinic.
   */
  clinicId?: ClinicId
  refetchInterval?: number
}) {
  const api = useOsApi()
  const { from, to, staffIds, clinicId } = opts

  return useQuery({
    queryKey: ['blocked-slots', from, to, staffIds, clinicId],
    enabled: !!from && !!to,
    refetchInterval: opts.refetchInterval,
    queryFn: async () => {
      const response = await api.get('/blocked-slots', {
        query: { from: from!, to: to!, staffIds, clinicId },
      })
      if (!response.success) throw new Error(response.error.message)
      return response.data.blockedSlots
    },
  })
}
